import {
  GOLD_500,
  MOON_500,
  RED_500,
  RED_600,
} from '@wandb/weave/common/css/color.styles';
import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {TextArea} from '@wandb/weave/components/Form/TextArea';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon, IconName} from '@wandb/weave/components/Icon';
import React, {useRef, useState} from 'react';

import {BORDER_COLOR, SECONDARY_BACKGROUND_COLOR} from './constants';

// Common styles for consistent UI
const UPPERCASE_LABEL_STYLE: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: MOON_500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

export const LoadingSelect: typeof Select = props => {
  return <Select isDisabled placeholder="Loading..." {...props} />;
};

export const FieldLabel: React.FC<{
  label: string;
  icon?: IconName;
  required?: boolean;
}> = ({label, icon, required}) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: 600,
        fontSize: '16px',
      }}>
      {icon && <Icon name={icon} size="small" />}
      <span>{label}</span>
      {required && <span style={{color: RED_500}}>*</span>}
    </div>
  );
};

const ValidationMessage: React.FC<{
  message: string;
  type: 'error' | 'warning' | 'info';
}> = ({message, type}) => {
  const colors = {
    error: RED_600,
    warning: GOLD_500,
    info: MOON_500,
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '4px',
        fontSize: '16px',
        lineHeight: '1.25',
        color: colors[type],
      }}>
      {/* <Icon name={icons[type]} size="small" /> */}
      <span>{message}</span>
    </div>
  );
};

const Instructions: React.FC<{instructions: string | React.ReactNode}> = ({
  instructions,
}) => {
  return (
    <div
      style={{
        fontSize: '14px',
        marginTop: '-8px',
        color: MOON_500,
      }}>
      {instructions}
    </div>
  );
};

export const LabeledTextField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: IconName;
  required?: boolean;
  error?: string;
  warning?: string;
  instructions?: string | React.ReactNode;
  disabled?: boolean;
  onBlur?: () => void;
}> = ({
  label,
  value,
  onChange,
  placeholder,
  icon,
  required,
  error,
  warning,
  instructions,
  disabled,
  onBlur,
}) => {
  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
      <FieldLabel label={label} icon={icon} required={required} />
      <TextField
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        errorState={!!error}
        onBlur={onBlur}
      />
      {error && <ValidationMessage message={error} type="error" />}
      {!error && warning && (
        <ValidationMessage message={warning} type="warning" />
      )}
      {!error && !warning && instructions && (
        <Instructions instructions={instructions} />
      )}
    </div>
  );
};

export const LabeledTextArea: React.FC<{
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  icon?: IconName;
  required?: boolean;
  error?: string;
  warning?: string;
  instructions?: string | React.ReactNode;
  disabled?: boolean;
  rows?: number;
  onBlur?: () => void;
}> = ({
  label,
  value,
  onChange,
  placeholder,
  icon,
  required,
  error,
  warning,
  instructions,
  disabled,
  rows = 3,
  onBlur,
}) => {
  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
      <FieldLabel label={label} icon={icon} required={required} />
      <div
        style={{
          position: 'relative',
          border: error ? `2px solid ${RED_500}` : undefined,
          borderRadius: error ? '5px' : undefined,
        }}>
        <TextArea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          onBlur={onBlur}
          style={{
            border: error ? 'none' : undefined,
            outline: error ? 'none' : undefined,
          }}
        />
      </div>
      {error && <ValidationMessage message={error} type="error" />}
      {!error && warning && (
        <ValidationMessage message={warning} type="warning" />
      )}
      {!error && !warning && instructions && (
        <Instructions instructions={instructions} />
      )}
    </div>
  );
};

export const PickerContainer: React.FC<{
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({title, children, style}) => {
  return (
    <div
      style={{
        backgroundColor: SECONDARY_BACKGROUND_COLOR,
        borderRadius: '4px',
        ...style,
      }}>
      <div
        style={{
          ...UPPERCASE_LABEL_STYLE,
          marginBottom: '8px',
        }}>
        {title}
      </div>
      {children}
    </div>
  );
};

export const LabeledTextAreaWithVariableInsertion: React.FC<{
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  icon?: IconName;
  required?: boolean;
  error?: string;
  warning?: string;
  instructions?: string | React.ReactNode;
  disabled?: boolean;
  rows?: number;
  onBlur?: () => void;
  availableVariables: string[];
}> = ({
  label,
  value,
  onChange,
  placeholder,
  icon,
  required,
  error,
  warning,
  instructions,
  disabled,
  rows = 3,
  onBlur,
  availableVariables,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const insertVariable = (variable: string) => {
    if (!textAreaRef.current) return;

    const textarea = textAreaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const textBefore = value.substring(0, start);
    const textAfter = value.substring(end);
    const newValue = textBefore + `{${variable}}` + textAfter;

    // Create a synthetic event to match the expected onChange signature
    const syntheticEvent = {
      target: {value: newValue},
    } as React.ChangeEvent<HTMLTextAreaElement>;

    onChange(syntheticEvent);

    // Set cursor position after the inserted variable
    setTimeout(() => {
      const newPosition = start + variable.length + 2; // +2 for the braces
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    }, 0);

    setShowDropdown(false);
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <FieldLabel label={label} icon={icon} required={required} />
        <div ref={dropdownRef} style={{position: 'relative'}}>
          <Button
            variant="ghost"
            size="small"
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={disabled || availableVariables.length === 0}
            tooltip={
              availableVariables.length === 0
                ? 'No variables available'
                : 'Insert a variable at cursor position'
            }>
            Insert variable
          </Button>
          {showDropdown && availableVariables.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                zIndex: 1000,
                backgroundColor: 'white',
                border: `1px solid ${BORDER_COLOR}`,
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                minWidth: '150px',
                maxHeight: '200px',
                overflowY: 'auto',
              }}>
              {availableVariables.map(variable => (
                <div
                  key={variable}
                  style={{
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                  onClick={() => insertVariable(variable)}>
                  {'{' + variable + '}'}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          position: 'relative',
          border: error ? `2px solid ${RED_500}` : undefined,
          borderRadius: error ? '5px' : undefined,
        }}>
        <textarea
          ref={textAreaRef}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          onBlur={onBlur}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '5px',
            border: error ? 'none' : '1px solid #d1d5db',
            outline: error ? 'none' : undefined,
            resize: 'vertical',
            fontFamily: 'inherit',
            fontSize: '16px',
            lineHeight: '1.5',
          }}
        />
      </div>
      {error && <ValidationMessage message={error} type="error" />}
      {!error && warning && (
        <ValidationMessage message={warning} type="warning" />
      )}
      {!error && !warning && instructions && (
        <Instructions instructions={instructions} />
      )}
    </div>
  );
};
